/**
 * Payment-method + tax matching
 * ------------------------------
 * Pure helpers (unit-tested in paymentTaxMatch.test.ts) that map extracted document
 * text to the master lists:
 *   - matchPaymentMethod -> acctg_payment_method.payment_method_id (via match_aliases)
 *   - matchTax           -> { tax_type_id, tax_rate_id, rate_percent, tax_amount }
 *     by the printed rate %, narrowed by tax-type keyword and (when available) region.
 */

export interface PaymentMethodRow {
  payment_method_id: number;
  method_code: string;
  method_name: string;
  match_aliases?: string;   // pipe-separated, lowercase
  is_deleted?: number;
}

export interface TaxTypeRow { tax_type_id: number; tax_code: string; is_deleted?: number; }
export interface TaxRateRow {
  tax_rate_id: number;
  tax_type_id: number;
  rate_percent: number;
  label?: string;
  country_id?: number | null;
  state_id?: number | null;
  is_deleted?: number;
}

const lc = (s: string) => (s || '').toLowerCase();

// Whole-token-ish contains: for an alnum-only alias enforce boundaries; otherwise
// (aliases with punctuation like "m/c") a plain substring check is fine.
function aliasHit(hay: string, alias: string): boolean {
  const a = alias.trim();
  if (!a) return false;
  if (/^[a-z0-9 ]+$/.test(a)) {
    const re = new RegExp(`(^|[^a-z0-9])${a.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}([^a-z0-9]|$)`, 'i');
    return re.test(hay);
  }
  return hay.includes(a);
}

/** Best payment method whose alias appears in the text; prefers the longest matched alias. */
export function matchPaymentMethod(text: string, methods: PaymentMethodRow[]): number | null {
  const hay = lc(text);
  if (!hay) return null;
  let bestId: number | null = null;
  let bestLen = 0;
  for (const m of methods) {
    if (Number(m.is_deleted) === 1) continue;
    const aliases = (m.match_aliases || m.method_name || '').split('|');
    for (const al of aliases) {
      const a = lc(al);
      if (a.length > bestLen && aliasHit(hay, a)) { bestId = m.payment_method_id; bestLen = a.length; }
    }
  }
  return bestId;
}

const TAX_KEYWORDS: { code: string; re: RegExp }[] = [
  { code: 'HST', re: /\bhst\b/i },
  { code: 'GST', re: /\bgst\b/i },
  { code: 'QST', re: /\bqst\b|\btvq\b/i },
  { code: 'PST', re: /\bpst\b|\brst\b/i },
  { code: 'VAT', re: /\bvat\b/i },
  { code: 'SALES_TAX', re: /\bsales\s*tax\b/i },
];

/** Detect a percentage in the text (e.g. "13%", "9.975 %"). */
export function detectRatePercent(text: string): number | null {
  const m = (text || '').match(/(\d{1,2}(?:\.\d{1,3})?)\s*%/);
  return m ? parseFloat(m[1]) : null;
}

export interface TaxMatch { tax_type_id: number | null; tax_rate_id: number | null; rate_percent: number | null; tax_amount: number | null; }

/**
 * Match an extracted tax to the masters.
 * opts: { text, types, rates, ratePercent?, taxAmount?, countryId?, stateId? }
 * Strategy: find the rate by printed % ; if several share the %, prefer the one whose
 * tax-type keyword is in the text, then the one matching the given region, else first.
 */
export function matchTax(opts: {
  text?: string;
  types: TaxTypeRow[];
  rates: TaxRateRow[];
  ratePercent?: number | null;
  taxAmount?: number | null;
  countryId?: number | null;
  stateId?: number | null;
}): TaxMatch {
  const text = opts.text || '';
  const rate = opts.ratePercent != null ? opts.ratePercent : detectRatePercent(text);
  const result: TaxMatch = { tax_type_id: null, tax_rate_id: null, rate_percent: rate, tax_amount: opts.taxAmount ?? null };

  // Tax-type keyword (for narrowing + a type-only fallback).
  const kw = TAX_KEYWORDS.find(k => k.re.test(text));
  const kwTypeId = kw ? (opts.types.find(t => t.tax_code === kw.code && Number(t.is_deleted) !== 1)?.tax_type_id ?? null) : null;

  if (rate != null) {
    const eps = 0.01;
    let matches = (opts.rates || []).filter(r => Number(r.is_deleted) !== 1 && Math.abs(Number(r.rate_percent) - rate) < eps);
    if (matches.length > 1 && kwTypeId) {
      const byType = matches.filter(r => r.tax_type_id === kwTypeId);
      if (byType.length) matches = byType;
    }
    if (matches.length > 1 && opts.stateId != null) {
      const byState = matches.filter(r => r.state_id === opts.stateId);
      if (byState.length) matches = byState;
    }
    if (matches.length > 1 && opts.countryId != null) {
      const byCountry = matches.filter(r => r.country_id === opts.countryId);
      if (byCountry.length) matches = byCountry;
    }
    if (matches.length) {
      result.tax_rate_id = matches[0].tax_rate_id;
      result.tax_type_id = matches[0].tax_type_id;
      return result;
    }
  }
  // No rate match → at least surface the tax type from the keyword.
  result.tax_type_id = kwTypeId;
  return result;
}
